<script setup>
/**
 * CartLineRow Component
 * Renders a cart line group (one row per makat).
 * Supports unified over-max card when both promo and over-max lines exist.
 *
 * Receives a `group` from pricedCartGroups:
 * - makat, totalQty, item, promoLine, overMaxLine, regularLine, isUnifiedOverMaxCard, promoMax
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useCartStore } from '../../../app/store/cart.js'
import { VAT_MULTIPLIER, DEPOSIT_COEFF } from '../../../core/config/pricingConstants.js'
import { formatPrice } from '@core/utils/formatting.js'

const cartStore = useCartStore()

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  variant: {
    type: String,
    default: 'standalone', // 'standalone' = full card chrome, 'embedded' = compact for promo container
    validator: (v) => ['standalone', 'embedded'].includes(v),
  },
  tutorialConfig: {
    type: Object,
    default: null,
  },
  additionalProductsInfo: {
    type: Object,
    default: null,
  },
  showAdditionalTutorial: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['inc', 'dec', 'set-qty', 'remove', 'view-additional-products'])

// Local qty for input (bound to group.totalQty)
const localQty = ref(props.group.totalQty)
const validationError = ref(null)
const isShaking = ref(false)
const showBoxMessage = ref(false)

// Max-crossing notification state
const prevQty = ref(props.group.totalQty)
const showMaxCrossNotice = ref(false)
const isInitialized = ref(false)

// Initialize on mount to prevent flash on initial render
onMounted(() => {
  prevQty.value = props.group.totalQty
  isInitialized.value = true
})

// Trigger shake animation
function triggerShake() {
  isShaking.value = true
  setTimeout(() => {
    isShaking.value = false
  }, 400)
}

// Show box multiples message temporarily
function flashBoxMessage() {
  showBoxMessage.value = true
  setTimeout(() => {
    showBoxMessage.value = false
  }, 3000)
}

// Watch for external qty changes and detect max crossing
watch(
  () => props.group.totalQty,
  (newQty, oldQty) => {
    localQty.value = newQty
    validationError.value = null

    // Only detect crossing after component is initialized (prevents flash on mount)
    if (isInitialized.value) {
      const max = props.group.promoMax
      if (max && props.group.isUnifiedOverMaxCard) {
        const wasAtOrBelowMax = prevQty.value <= max
        const nowAboveMax = newQty > max
        if (wasAtOrBelowMax && nowAboveMax) {
          showMaxCrossNotice.value = true
          setTimeout(() => {
            showMaxCrossNotice.value = false
          }, 3000)
        }
      }
    }

    prevQty.value = newQty
  }
)

// Variant helpers
const isEmbedded = computed(() => props.variant === 'embedded')

// Additional products button (only for standalone variant)
const showAdditionalProducts = computed(() => {
  return !isEmbedded.value && !!props.additionalProductsInfo
})

const additionalProductsTutorial = computed(() => {
  if (!props.showAdditionalTutorial) return null
  return {
    id: 'cart-additional-products',
    text: 'לחצו כאן כדי לראות מוצרים נוספים שמשתתפים באותו מבצע או הנחת כמות',
    importance: 'info',
    showOnce: true,
    autoTrigger: true,
    group: 'cart-intro',
    step: 5,
  }
})

function handleViewAdditionalProducts() {
  emit('view-additional-products', props.additionalProductsInfo)
}

// Group properties
const makat = computed(() => props.group.makat)
const itemName = computed(() => props.group.item?.name || 'פריט לא ידוע')
const itemImage = computed(() => props.group.item?.image || null)
const totalQty = computed(() => props.group.totalQty)
const isUnified = computed(() => props.group.isUnifiedOverMaxCard)

// Get the primary display line (promoLine, discountLine, regularLine, or overMaxLine)
const primaryLine = computed(() => {
  return (
    props.group.promoLine ||
    props.group.discountLine ||
    props.group.regularLine ||
    props.group.overMaxLine
  )
})

// Check if price exists
const hasPrice = computed(() => {
  const line = primaryLine.value
  return line?.unitPricePreVatPreDeposit !== null && line?.unitPricePreVatPreDeposit !== undefined
})

// Is promo active?
const hasPromo = computed(() => !!props.group.promoLine)

// Is discount active? (only when no promo)
const hasDiscount = computed(() => !hasPromo.value && !!props.group.discountLine)

// Promo label
const promoLabel = computed(() => {
  const promo = props.group.promoLine?.promo
  if (!promo) return null
  if (promo.first_order) return 'הזמנה ראשונה'
  if (promo.weeklyBenefit) return 'הטבה שבועית'
  return 'מבצע'
})

// Discount label (shows percentage)
const discountLabel = computed(() => {
  const discount = props.group.discountLine?.discount
  if (!discount) return null
  return `-${discount.discountPercent}%`
})

// onlyBox rules - 0, '0', false, null, or undefined means box multiples required
const requiresBoxMultiples = computed(() => {
  const val = props.group.item?.onlyBox
  return val == null || val === 0 || val === '0' || val === false
})

const boxSize = computed(() => {
  if (!props.group.item?.boxSize) return 1
  const parsed = parseInt(props.group.item.boxSize, 10)
  return isNaN(parsed) || parsed <= 0 ? 1 : parsed
})

const stepSize = computed(() => {
  return requiresBoxMultiples.value ? boxSize.value : 1
})

// Unified card totals (comprehensive breakdown)
const unifiedBreakdown = computed(() => {
  if (!isUnified.value) return null

  const promoLine = props.group.promoLine
  const overMaxLine = props.group.overMaxLine
  const item = props.group.item

  // Promo section
  const promoQty = promoLine?.qty || 0
  const promoUnitPrice = promoLine?.unitPricePreVatPreDeposit || 0
  const promoItemsTotal = promoUnitPrice * promoQty

  // Over-max section
  const overMaxQty = overMaxLine?.qty || 0
  const overMaxUnitPrice = overMaxLine?.unitPricePreVatPreDeposit || 0
  const overMaxItemsTotal = overMaxUnitPrice * overMaxQty

  // Combined items total (pre-VAT, pre-deposit)
  const combinedItemsSubtotalPreVat = promoItemsTotal + overMaxItemsTotal

  // Deposit calculation
  const pikadon = item?.pikadon ? parseFloat(item.pikadon) : 0
  const depositPerUnit = isNaN(pikadon) ? 0 : pikadon * DEPOSIT_COEFF
  const totalQty = promoQty + overMaxQty
  const depositTotal = depositPerUnit * totalQty

  // Total pre-VAT (items + deposit)
  const totalPreVat = combinedItemsSubtotalPreVat + depositTotal

  // Total with VAT
  const totalWithVat = totalPreVat * VAT_MULTIPLIER

  // Average unit price (for mixed pricing display)
  const avgUnitPrice = totalQty > 0 ? combinedItemsSubtotalPreVat / totalQty : 0

  return {
    promoUnitPrice,
    overMaxUnitPrice,
    avgUnitPrice,
    depositTotal,
    totalPreVat,
    totalWithVat,
    hasDeposit: depositPerUnit > 0,
  }
})

// Legacy: simple total (for backwards compatibility)
const unifiedTotalPreVat = computed(() => {
  if (!isUnified.value) return null
  return unifiedBreakdown.value?.totalPreVat || 0
})

// Next step hint (promo or amount discount)
const nextHint = computed(() => {
  if (isEmbedded.value) return null

  if (props.group.nextPromoHint) {
    return {
      type: 'promo',
      message: `קנה עוד ${props.group.nextPromoHint.unitsNeeded} יח' ותקבל מחיר של ${props.group.nextPromoHint.nextPrice} ₪ ליחידה`,
    }
  }
  if (props.group.nextDiscountHint) {
    return {
      type: 'discount',
      message: `קנה עוד ${props.group.nextDiscountHint.unitsNeeded} יח' ותקבל הנחה של ${props.group.nextDiscountHint.nextDiscountPercent}%`,
    }
  }
  return null
})

// Validate quantity
function validateQty(qty) {
  const intQty = Math.floor(qty)
  if (intQty <= 0) {
    return { valid: false, message: 'כמות חייבת להיות לפחות 1' }
  }
  if (requiresBoxMultiples.value) {
    if (intQty % boxSize.value !== 0) {
      return { valid: false, message: `בפריט זה ניתן להזמין רק במכפלות של ${boxSize.value}` }
    }
  }
  return { valid: true }
}

// Handle manual qty input
function handleQtyInput(event) {
  const raw = event.target.value
  // Allow empty field while user is typing
  if (raw === '' || raw == null) {
    localQty.value = null
    validationError.value = null
    return
  }
  const value = parseInt(raw, 10)
  if (isNaN(value)) {
    return
  }
  localQty.value = value
  const validation = validateQty(value)
  if (!validation.valid) {
    validationError.value = validation.message
  } else {
    validationError.value = null
    emit('set-qty', makat.value, value)
  }
}

// Handle blur — revert to last valid qty if empty or invalid
function handleQtyBlur() {
  if (localQty.value == null || localQty.value <= 0 || validationError.value) {
    localQty.value = totalQty.value
    triggerShake()
    if (validationError.value) {
      setTimeout(() => {
        validationError.value = null
      }, 2000)
    }
  }
}

// Increment
function handleInc() {
  validationError.value = null
  // Show box message only for onlyBox items
  if (requiresBoxMultiples.value) {
    flashBoxMessage()
  }
  emit('inc', makat.value)
}

// Decrement
function handleDec() {
  validationError.value = null
  // Show box message only for onlyBox items
  if (requiresBoxMultiples.value) {
    flashBoxMessage()
  }
  emit('dec', makat.value)
}

// Remove
function handleRemove() {
  emit('remove', makat.value)
}
</script>

<template>
  <!-- UNIFIED OVER-MAX CARD: promo + over-max in one card -->
  <div
    v-if="isUnified"
    v-tutorial="tutorialConfig"
    class="cart-line cart-line-unified"
    :class="{ 'has-error': validationError || !hasPrice, 'variant-embedded': isEmbedded }"
  >
    <!-- Name Row (top) -->
    <div class="line-name-row">
      <div class="line-name">{{ itemName }}</div>
      <button class="remove-btn" aria-label="הסר פריט" @click="handleRemove">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      </button>
    </div>

    <!-- Header: Image + Qty -->
    <div class="unified-header">
      <div class="line-image">
        <img v-if="itemImage" :src="itemImage" :alt="itemName" />
        <div v-else class="image-placeholder">
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
      </div>
      <div class="unified-title">
        <div class="unified-total-qty">סה"כ: {{ totalQty }} יח'</div>
        <button
          v-if="showAdditionalProducts"
          v-tutorial="additionalProductsTutorial"
          class="additional-products-btn"
          @click.stop="handleViewAdditionalProducts"
        >
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
          <span>מוצרים נוספים</span>
        </button>
      </div>
    </div>

    <!-- Pricing Sections -->
    <div class="unified-sections">
      <!-- Section A: Promo -->
      <div class="unified-section promo-section">
        <div class="section-header">
          <span class="promo-badge">{{ promoLabel }}</span>
          <span class="section-qty">{{ group.promoLine.qty }} יח'</span>
        </div>
        <div class="section-pricing">
          <span class="section-unit-price"
            >{{ formatPrice(group.promoLine.unitPricePreVatPreDeposit) }} ₪ ליח'</span
          >
          <span class="section-total"
            >{{
              formatPrice(group.promoLine.unitPricePreVatPreDeposit * group.promoLine.qty)
            }}
            ₪</span
          >
        </div>
      </div>

      <!-- Section B: Over-max Regular -->
      <div class="unified-section overmax-section">
        <div class="section-header">
          <span class="over-max-badge">מעל מקסימום – מחיר רגיל</span>
          <span class="section-qty">{{ group.overMaxLine.qty }} יח'</span>
        </div>
        <div class="section-pricing">
          <span class="section-unit-price"
            >{{ formatPrice(group.overMaxLine.unitPricePreVatPreDeposit) }} ₪ ליח'</span
          >
          <span class="section-total"
            >{{
              formatPrice(group.overMaxLine.unitPricePreVatPreDeposit * group.overMaxLine.qty)
            }}
            ₪</span
          >
        </div>
      </div>
    </div>

    <!-- Max Crossing Notice -->
    <Transition name="notice">
      <div v-if="showMaxCrossNotice" class="max-cross-notice">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>שים לב: יחידות מעבר ל-{{ group.promoMax }} יח' יחויבו במחיר רגיל.</span>
      </div>
    </Transition>

    <!-- TOTAL Breakdown Row (matching Item Dialog design) -->
    <div v-if="unifiedBreakdown" class="unified-breakdown">
      <!-- Unit Prices (stacked: promo + regular) -->
      <div class="breakdown-col">
        <span class="breakdown-label">מחיר ליח'</span>
        <div class="breakdown-prices-stacked">
          <span class="price-promo"
            >מבצע: {{ formatPrice(unifiedBreakdown.promoUnitPrice) }} ₪</span
          >
          <span class="price-regular"
            >רגיל: {{ formatPrice(unifiedBreakdown.overMaxUnitPrice) }} ₪</span
          >
        </div>
      </div>

      <!-- Deposit (if exists) -->
      <div v-if="unifiedBreakdown.hasDeposit" class="breakdown-col">
        <span class="breakdown-label">פיקדון</span>
        <span class="breakdown-value">{{ formatPrice(unifiedBreakdown.depositTotal) }} ₪</span>
      </div>

      <!-- Total Pre-VAT -->
      <div class="breakdown-col">
        <span class="breakdown-label">סה"כ לפני מע"מ</span>
        <span class="breakdown-value">{{ formatPrice(unifiedBreakdown.totalPreVat) }} ₪</span>
      </div>

      <!-- Total With VAT -->
      <div class="breakdown-col breakdown-col-total">
        <span class="breakdown-label">סה"כ כולל מע"מ</span>
        <span class="breakdown-value breakdown-value-total"
          >{{ formatPrice(unifiedBreakdown.totalWithVat) }} ₪</span
        >
      </div>
    </div>

    <!-- Stepper + Validation -->
    <div class="unified-footer">
      <div v-if="validationError" class="validation-error">{{ validationError }}</div>
      <div class="line-qty" :class="{ shaking: isShaking }">
        <button
          class="qty-btn qty-dec"
          :disabled="!hasPrice || totalQty <= stepSize"
          @click="handleDec"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M5 12h14" />
          </svg>
        </button>
        <input
          type="number"
          class="qty-input"
          :value="localQty"
          :disabled="!hasPrice"
          :step="stepSize"
          @input="handleQtyInput"
          @blur="handleQtyBlur"
        />
        <button class="qty-btn qty-inc" :disabled="!hasPrice" @click="handleInc">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Next Step Hint (unified card) -->
    <Transition name="hint-fade">
      <div v-if="nextHint" class="next-step-hint" :class="nextHint.type">
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
          />
        </svg>
        <span>{{ nextHint.message }}</span>
      </div>
    </Transition>

    <!-- Box Multiples Toast (unified card) -->
    <Transition name="box-toast">
      <div v-if="requiresBoxMultiples && showBoxMessage" class="box-message">
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>בפריט זה ניתן להזמין רק במכפלות של {{ boxSize }}</span>
      </div>
    </Transition>
  </div>

  <!-- SIMPLE CARD: normal single line (promo, discount, or regular, no over-max split) -->
  <div
    v-else
    v-tutorial="tutorialConfig"
    class="cart-line"
    :class="{
      'has-error': validationError || !hasPrice,
      'is-promo-line': hasPromo,
      'is-discount-line': hasDiscount,
      'variant-embedded': isEmbedded,
    }"
  >
    <!-- Name Row (top) -->
    <div class="line-name-row">
      <div class="line-name">{{ itemName }}</div>
      <button class="remove-btn" aria-label="הסר פריט" @click="handleRemove">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      </button>
    </div>

    <!-- Content Row: Image + Details -->
    <div class="line-content-row">
      <!-- Item Image -->
      <div class="line-image">
        <img v-if="itemImage" :src="itemImage" :alt="itemName" />
        <div v-else class="image-placeholder">
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
      </div>

      <!-- Item Details -->
      <div class="line-details">
        <div
          class="line-price"
          :class="{ 'no-price': !hasPrice, 'has-promo': hasPromo, 'has-discount': hasDiscount }"
        >
          <template v-if="hasPrice">
            <!-- Original price (strike-through when promo or discount) -->
            <span
              v-if="
                (hasPromo || hasDiscount) &&
                primaryLine.catalogPrice !== primaryLine.unitPricePreVatPreDeposit
              "
              class="price-original"
              >{{ formatPrice(primaryLine.catalogPrice) }}</span
            >
            <span
              class="price-value"
              :class="{ 'promo-price': hasPromo, 'discount-price': hasDiscount }"
              >{{ formatPrice(primaryLine.unitPricePreVatPreDeposit) }}</span
            >
            <span class="price-currency">₪</span>
            <span v-if="hasPromo" class="promo-badge">{{ promoLabel }}</span>
            <span v-if="hasDiscount" class="discount-badge">{{ discountLabel }}</span>
            <button
              v-if="showAdditionalProducts"
              v-tutorial="additionalProductsTutorial"
              class="additional-products-btn"
              @click.stop="handleViewAdditionalProducts"
            >
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
              <span>מוצרים נוספים</span>
            </button>
          </template>
          <template v-else>
            <span class="price-missing">אין מחיר</span>
          </template>
        </div>
        <div v-if="validationError" class="validation-error">{{ validationError }}</div>
        <div v-if="!hasPrice" class="price-error">לא ניתן לעדכן פריט ללא מחיר</div>
      </div>
    </div>

    <!-- Actions Row: Stepper + Total -->
    <div class="line-actions-row">
      <!-- Qty Stepper -->
      <div class="line-qty" :class="{ shaking: isShaking }">
        <button
          class="qty-btn qty-dec"
          :disabled="!hasPrice || totalQty <= stepSize"
          @click="handleDec"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M5 12h14" />
          </svg>
        </button>
        <input
          type="number"
          class="qty-input"
          :value="localQty"
          :disabled="!hasPrice"
          :step="stepSize"
          @input="handleQtyInput"
          @blur="handleQtyBlur"
        />
        <button class="qty-btn qty-inc" :disabled="!hasPrice" @click="handleInc">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>

      <!-- Line Total -->
      <div class="line-total" :class="{ 'no-price': !hasPrice }">
        <template v-if="hasPrice">
          <span class="total-value">{{
            formatPrice(primaryLine.unitPricePreVatPreDeposit * totalQty)
          }}</span>
          <span class="total-currency">₪</span>
        </template>
        <template v-else>
          <span class="total-missing">—</span>
        </template>
      </div>
    </div>

    <!-- Next Step Hint (simple card) -->
    <Transition name="hint-fade">
      <div v-if="nextHint" class="next-step-hint" :class="nextHint.type">
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
          />
        </svg>
        <span>{{ nextHint.message }}</span>
      </div>
    </Transition>

    <!-- Box Multiples Toast -->
    <Transition name="box-toast">
      <div v-if="requiresBoxMultiples && showBoxMessage" class="box-message">
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>בפריט זה ניתן להזמין רק במכפלות של {{ boxSize }}</span>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.cart-line {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  transition: all var(--transition-fast);
}

.cart-line:hover {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-sm);
}

.cart-line.has-error {
  border-color: var(--color-error);
  background: rgba(239, 68, 68, 0.03);
}

/* Name Row (top of card) */
.line-name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.line-name-row .line-name {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  line-height: 1.3;
}

.line-name-row .remove-btn {
  flex-shrink: 0;
}

/* Content Row (image + details) */
.line-content-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

/* Actions Row (stepper + total) */
.line-actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding-top: var(--spacing-xs);
  border-top: 1px solid var(--border-color);
}

/* Image */
.line-image {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  background: var(--bg-secondary);
}

.line-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #ffffff;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
}

/* Details */
.line-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.line-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  line-height: 1.3;
}

.line-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.line-price.no-price {
  color: var(--color-error);
}

.line-price.has-promo {
  flex-wrap: wrap;
}

.price-missing {
  font-weight: var(--font-weight-medium);
}

.price-original {
  text-decoration: line-through;
  color: var(--text-muted);
  margin-left: var(--spacing-xs);
}

.price-value.promo-price {
  color: #dc2626;
  font-weight: var(--font-weight-semibold);
}

.price-value.discount-price {
  color: #059669;
  font-weight: var(--font-weight-semibold);
}

.promo-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  margin-right: var(--spacing-xs);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius-full);
  white-space: nowrap;
}

.discount-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  margin-right: var(--spacing-xs);
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius-full);
  white-space: nowrap;
}

/* Discount line styling */
.cart-line.is-discount-line {
  border-color: rgba(16, 185, 129, 0.3);
}

.cart-line.is-discount-line:hover {
  border-color: rgba(16, 185, 129, 0.5);
}

.line-price.has-discount {
  flex-wrap: wrap;
}

.validation-error,
.price-error {
  font-size: 11px;
  color: var(--color-error);
  margin-top: 2px;
}

/* Qty Stepper */
.line-qty {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.line-qty.shaking {
  animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

.line-qty.shaking .qty-input {
  border-color: var(--color-error);
  background: rgba(239, 68, 68, 0.05);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-3px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(3px);
  }
}

.qty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.qty-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-color: var(--color-primary-light);
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qty-input {
  width: 48px;
  height: 28px;
  padding: 0 4px;
  text-align: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  transition: all var(--transition-fast);
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(40, 53, 72, 0.1);
}

.qty-input:disabled {
  background: var(--bg-secondary);
  opacity: 0.5;
}

/* Line Total */
.line-total {
  display: flex;
  align-items: baseline;
  gap: 2px;
  flex-shrink: 0;
  min-width: 70px;
  justify-content: flex-end;
}

.total-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.total-currency {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.line-total.no-price {
  color: var(--color-error);
}

/* Remove Button */
.remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.remove-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
}

/* ============================================
   UNIFIED OVER-MAX CARD STYLES
   ============================================ */
.cart-line-unified {
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
}

.unified-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.unified-header .line-image {
  flex-shrink: 0;
}

.unified-title {
  flex: 1;
  min-width: 0;
}

.unified-title .line-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.unified-total-qty {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 2px;
}

.unified-sections {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-sm);
  width: 100%;
}

.unified-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
}

.promo-section {
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.overmax-section {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.2);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.section-qty {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.section-pricing {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.section-unit-price {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.section-total {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

/* Max Crossing Notice */
.max-cross-notice {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  color: #92400e;
  width: 100%;
}

.max-cross-notice svg {
  flex-shrink: 0;
  color: #f59e0b;
}

.notice-enter-active,
.notice-leave-active {
  transition: all 0.3s ease;
}

.notice-enter-from,
.notice-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* TOTAL Breakdown Row */
.unified-breakdown {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
  width: 100%;
  flex-wrap: wrap;
}

.breakdown-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  text-align: center;
}

.breakdown-col-total {
  flex-shrink: 0;
}

.breakdown-label {
  font-size: 10px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.breakdown-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.breakdown-value-total {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.breakdown-prices-stacked {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.breakdown-prices-stacked .price-promo {
  font-size: 11px;
  color: #16a34a;
  font-weight: var(--font-weight-medium);
}

.breakdown-prices-stacked .price-regular {
  font-size: 11px;
  color: var(--text-secondary);
}

.unified-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm);
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
  width: 100%;
}

.summary-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.summary-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.unified-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
}

.unified-footer .line-qty {
  width: auto;
}

/* Over-max badge (used in unified and standalone) */
.over-max-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  border-radius: var(--border-radius-full);
  white-space: nowrap;
}

/* Box Multiples Toast */
.box-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  color: #1d4ed8;
  width: 100%;
}

.box-message svg {
  flex-shrink: 0;
  color: #3b82f6;
}

/* Box Toast Transition */
.box-toast-enter-active,
.box-toast-leave-active {
  transition: all 0.3s ease;
}

.box-toast-enter-from,
.box-toast-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ============================================
   EMBEDDED VARIANT (for promo group containers)
   ============================================ */
.cart-line.variant-embedded {
  padding: var(--spacing-sm);
  border: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  border-bottom: 1px solid var(--border-color);
}

.cart-line.variant-embedded:last-child {
  border-bottom: none;
}

.cart-line.variant-embedded:hover {
  border-color: var(--border-color);
  box-shadow: none;
}

.cart-line.variant-embedded .line-image {
  width: 40px;
  height: 40px;
}

.cart-line.variant-embedded .line-name-row .line-name {
  font-size: var(--font-size-xs);
}

.cart-line.variant-embedded .line-actions-row {
  padding-top: var(--spacing-xs);
  border-top: none;
}

.cart-line.variant-embedded .qty-btn {
  width: 24px;
  height: 24px;
}

.cart-line.variant-embedded .qty-input {
  width: 40px;
  height: 24px;
  font-size: var(--font-size-xs);
}

.cart-line.variant-embedded .total-value {
  font-size: var(--font-size-sm);
}

.cart-line.variant-embedded .remove-btn {
  width: 28px;
  height: 28px;
}

.cart-line.variant-embedded .remove-btn svg {
  width: 14px;
  height: 14px;
}

/* Embedded unified card */
.cart-line-unified.variant-embedded {
  padding: var(--spacing-sm);
  border: none;
  background: transparent;
  box-shadow: none;
}

.cart-line-unified.variant-embedded:hover {
  border-color: transparent;
  box-shadow: none;
}

/* Mobile */
@media (max-width: 767px) {
  .cart-line {
    flex-wrap: wrap;
    gap: var(--spacing-sm);
  }

  .line-image {
    width: 48px;
    height: 48px;
  }

  .line-details {
    flex: 1;
    min-width: calc(100% - 60px - 40px);
  }

  .line-qty,
  .line-qty-readonly {
    order: 10;
  }

  .line-total {
    order: 11;
    min-width: 60px;
  }

  .remove-btn,
  .remove-placeholder {
    order: 5;
  }
}

/* Next step hint */
.next-step-hint {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  line-height: 1.3;
}

.next-step-hint.promo {
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.25);
  color: #15803d;
}

.next-step-hint.promo svg {
  color: #22c55e;
  flex-shrink: 0;
}

.next-step-hint.discount {
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.25);
  color: #1d4ed8;
}

.next-step-hint.discount svg {
  color: #3b82f6;
  flex-shrink: 0;
}

.hint-fade-enter-active,
.hint-fade-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}

.hint-fade-enter-from,
.hint-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Additional Products Button */
.additional-products-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  margin-inline-start: auto;
  background: rgba(40, 53, 72, 0.08);
  border: 1px solid rgba(40, 53, 72, 0.2);
  border-radius: var(--border-radius-sm);
  color: #1e293b;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.additional-products-btn:hover {
  background: rgba(40, 53, 72, 0.15);
  border-color: rgba(40, 53, 72, 0.4);
}

.additional-products-btn svg {
  flex-shrink: 0;
  color: #283548;
}
</style>
